/**
 * Dotypos Export Dialog - Push employees from TagInn to Dotypos POS
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  CheckCircle2,
  XCircle,
  AlertCircle,
  Loader2,
  Upload,
  ChevronRight,
  ChevronLeft,
  Users,
  RefreshCw,
  Link2,
  ArrowUpRight,
} from 'lucide-react';
import { DotyposService } from '@/lib/integrations/dotypos-service';
import { useDotyposEmployeeExport } from '@/hooks/integrations/useDotyposEmployeeExport';
import {
  ExportStep,
  ExportWizardState,
  PushEmployeesSummary,
  EmployeeSyncInfo,
} from '@/types/dotypos';
import { Employee } from '@/types';
import { cn } from '@/lib/core/utils';
import { supabase } from '@/integrations/supabase/client';

interface DotyposExportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  businessId: string;
  businessName?: string;
  preSelectedEmployeeIds?: string[];
  onExportComplete?: (summary: PushEmployeesSummary) => void;
}

export const DotyposExportDialog: React.FC<DotyposExportDialogProps> = ({
  isOpen,
  onClose,
  businessId,
  businessName = 'System',
  preSelectedEmployeeIds = [],
  onExportComplete,
}) => {
  const [wizardState, setWizardState] = useState<ExportWizardState>({
    step: 'select',
    selectedEmployeeIds: preSelectedEmployeeIds,
    isLoading: false,
    error: null,
  });

  const [employees, setEmployees] = useState<Employee[]>([]);
  const [syncStatuses, setSyncStatuses] = useState<Map<string, EmployeeSyncInfo>>(new Map());
  const [dotyposEnabled, setDotyposEnabled] = useState<boolean | null>(null);

  const { isPushing, pushEmployeesBulk, getSyncStatus } = useDotyposEmployeeExport();

  // Load employees and check Dotypos configuration
  useEffect(() => {
    if (isOpen) {
      loadEmployees();
      checkDotyposConfig();
    }
  }, [isOpen, businessId]);

  // Update selected employees when preSelectedEmployeeIds changes
  useEffect(() => {
    if (preSelectedEmployeeIds.length > 0) {
      setWizardState((prev) => ({
        ...prev,
        selectedEmployeeIds: preSelectedEmployeeIds,
      }));
    }
  }, [preSelectedEmployeeIds]);

  const checkDotyposConfig = async () => {
    const { data: business } = await supabase
      .from('businesses')
      .select('dotypos_enabled, dotypos_refresh_token, dotypos_cloud_id')
      .eq('id', businessId)
      .single();

    setDotyposEnabled(
      !!(business?.dotypos_enabled && business?.dotypos_refresh_token && business?.dotypos_cloud_id)
    );
  };

  const loadEmployees = async () => {
    setWizardState((prev) => ({ ...prev, isLoading: true }));
    try {
      // Get exportable employees (no placeholder emails)
      const exportable = await DotyposService.getExportableEmployees(businessId);
      setEmployees(exportable);

      // Get sync status for all employees
      const statuses = await getSyncStatus(
        businessId,
        exportable.map((e) => e.id)
      );
      const statusMap = new Map(statuses.map((s) => [s.employeeId, s]));
      setSyncStatuses(statusMap);
    } catch (error: any) {
      setWizardState((prev) => ({
        ...prev,
        error: error.message || 'Failed to load employees',
      }));
    } finally {
      setWizardState((prev) => ({ ...prev, isLoading: false }));
    }
  };

  const handleSelectAll = () => {
    setWizardState((prev) => ({
      ...prev,
      selectedEmployeeIds: employees.map((e) => e.id),
    }));
  };

  const handleDeselectAll = () => {
    setWizardState((prev) => ({
      ...prev,
      selectedEmployeeIds: [],
    }));
  };

  const handleToggleEmployee = (employeeId: string) => {
    setWizardState((prev) => {
      const isSelected = prev.selectedEmployeeIds.includes(employeeId);
      return {
        ...prev,
        selectedEmployeeIds: isSelected
          ? prev.selectedEmployeeIds.filter((id) => id !== employeeId)
          : [...prev.selectedEmployeeIds, employeeId],
      };
    });
  };

  const handleProceedToReview = () => {
    setWizardState((prev) => ({ ...prev, step: 'review' }));
  };

  const handleBackToSelect = () => {
    setWizardState((prev) => ({ ...prev, step: 'select' }));
  };

  const handleExport = async () => {
    setWizardState((prev) => ({ ...prev, step: 'export', isLoading: true }));

    try {
      const summary = await pushEmployeesBulk(businessId, wizardState.selectedEmployeeIds);

      if (summary) {
        setWizardState((prev) => ({
          ...prev,
          step: 'complete',
          isLoading: false,
          summary,
        }));

        if (onExportComplete) {
          onExportComplete(summary);
        }
      } else {
        setWizardState((prev) => ({
          ...prev,
          step: 'review',
          isLoading: false,
          error: 'Export failed',
        }));
      }
    } catch (error: any) {
      setWizardState((prev) => ({
        ...prev,
        step: 'review',
        isLoading: false,
        error: error.message || 'Export failed',
      }));
    }
  };

  const handleClose = () => {
    setWizardState({
      step: 'select',
      selectedEmployeeIds: [],
      isLoading: false,
      error: null,
    });
    onClose();
  };

  const getSyncStatusBadge = (employeeId: string) => {
    const status = syncStatuses.get(employeeId);
    if (!status) return null;

    switch (status.syncStatus) {
      case 'synced':
        return (
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
            <Link2 className="w-3 h-3 mr-1" />
            Synced
          </Badge>
        );
      case 'error':
        return (
          <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
            <XCircle className="w-3 h-3 mr-1" />
            Error
          </Badge>
        );
      case 'not_synced':
        return (
          <Badge variant="outline" className="bg-gray-50 text-gray-600 border-gray-200">
            Not synced
          </Badge>
        );
      default:
        return null;
    }
  };

  const selectedCount = wizardState.selectedEmployeeIds.length;
  const syncedCount = wizardState.selectedEmployeeIds.filter(
    (id) => syncStatuses.get(id)?.syncStatus === 'synced'
  ).length;
  const newCount = selectedCount - syncedCount;

  // Render different steps
  const renderSelectStep = () => (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Upload className="w-5 h-5" />
          Push Employees to Dotypos
        </DialogTitle>
        <DialogDescription>
          Select employees to push to your Dotypos POS system. Employees with placeholder emails
          are excluded.
        </DialogDescription>
      </DialogHeader>

      {dotyposEnabled === false && (
        <Alert variant="destructive" className="mt-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Dotypos integration is not configured. Please set up Dotypos in Settings first.
          </AlertDescription>
        </Alert>
      )}

      {wizardState.error && (
        <Alert variant="destructive" className="mt-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{wizardState.error}</AlertDescription>
        </Alert>
      )}

      <div className="flex items-center justify-between py-2">
        <span className="text-sm text-muted-foreground">
          {employees.length} employee(s) available
        </span>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleSelectAll}>
            Select All
          </Button>
          <Button variant="outline" size="sm" onClick={handleDeselectAll}>
            Deselect All
          </Button>
        </div>
      </div>

      <ScrollArea className="h-[300px] border rounded-md p-2">
        {wizardState.isLoading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : employees.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
            <Users className="w-8 h-8 mb-2" />
            <p>No employees available to export</p>
            <p className="text-xs">Employees with placeholder emails are excluded</p>
          </div>
        ) : (
          <div className="space-y-2">
            {employees.map((employee) => (
              <div
                key={employee.id}
                className={cn(
                  'flex items-center justify-between p-2 rounded-md border cursor-pointer hover:bg-accent',
                  wizardState.selectedEmployeeIds.includes(employee.id) && 'bg-accent'
                )}
                onClick={() => handleToggleEmployee(employee.id)}
              >
                <div className="flex items-center gap-3">
                  <Checkbox
                    checked={wizardState.selectedEmployeeIds.includes(employee.id)}
                    onCheckedChange={() => handleToggleEmployee(employee.id)}
                  />
                  <div>
                    <p className="font-medium">{employee.name}</p>
                    <p className="text-sm text-muted-foreground">{employee.email}</p>
                  </div>
                </div>
                {getSyncStatusBadge(employee.id)}
              </div>
            ))}
          </div>
        )}
      </ScrollArea>

      <DialogFooter className="gap-2">
        <Button variant="outline" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          onClick={handleProceedToReview}
          disabled={selectedCount === 0 || dotyposEnabled === false}
        >
          Review ({selectedCount})
          <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </DialogFooter>
    </>
  );

  const renderReviewStep = () => (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Users className="w-5 h-5" />
          Review Export
        </DialogTitle>
        <DialogDescription>
          Review the employees that will be pushed to Dotypos.
        </DialogDescription>
      </DialogHeader>

      <div className="grid grid-cols-3 gap-4 py-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <ArrowUpRight className="w-4 h-4 text-blue-500" />
              {newCount}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">New to push</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <RefreshCw className="w-4 h-4 text-green-500" />
              {syncedCount}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">To update</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-4 h-4 text-gray-500" />
              {selectedCount}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">Total selected</p>
          </CardContent>
        </Card>
      </div>

      <ScrollArea className="h-[200px] border rounded-md p-2">
        <div className="space-y-1">
          {wizardState.selectedEmployeeIds.map((id) => {
            const employee = employees.find((e) => e.id === id);
            const status = syncStatuses.get(id);
            if (!employee) return null;

            return (
              <div
                key={id}
                className="flex items-center justify-between p-2 text-sm"
              >
                <span>{employee.name}</span>
                <span className="text-muted-foreground">
                  {status?.syncStatus === 'synced' ? 'Will update' : 'Will create'}
                </span>
              </div>
            );
          })}
        </div>
      </ScrollArea>

      <DialogFooter className="gap-2">
        <Button variant="outline" onClick={handleBackToSelect}>
          <ChevronLeft className="w-4 h-4 mr-1" />
          Back
        </Button>
        <Button onClick={handleExport} disabled={isPushing}>
          {isPushing ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Pushing...
            </>
          ) : (
            <>
              <Upload className="w-4 h-4 mr-2" />
              Push to Dotypos
            </>
          )}
        </Button>
      </DialogFooter>
    </>
  );

  const renderExportStep = () => (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Loader2 className="w-5 h-5 animate-spin" />
          Pushing to Dotypos...
        </DialogTitle>
        <DialogDescription>Please wait while employees are being pushed to Dotypos.</DialogDescription>
      </DialogHeader>

      <div className="py-8 flex flex-col items-center justify-center">
        <Progress value={undefined} className="w-full" />
        <p className="mt-4 text-sm text-muted-foreground">
          Pushing {selectedCount} employee(s)...
        </p>
      </div>
    </>
  );

  const renderCompleteStep = () => {
    const summary = wizardState.summary;
    if (!summary) return null;

    const hasErrors = summary.failed > 0;

    return (
      <>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {hasErrors ? (
              <AlertCircle className="w-5 h-5 text-amber-500" />
            ) : (
              <CheckCircle2 className="w-5 h-5 text-green-500" />
            )}
            Export Complete
          </DialogTitle>
          <DialogDescription>
            {hasErrors
              ? 'Export completed with some errors'
              : 'All employees were successfully pushed to Dotypos'}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4 py-4">
          <Card className="bg-green-50 border-green-200">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-green-700">{summary.created}</div>
              <p className="text-sm text-green-600">Created</p>
            </CardContent>
          </Card>
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-blue-700">{summary.updated}</div>
              <p className="text-sm text-blue-600">Updated</p>
            </CardContent>
          </Card>
          <Card className="bg-purple-50 border-purple-200">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-purple-700">{summary.linked}</div>
              <p className="text-sm text-purple-600">Linked</p>
            </CardContent>
          </Card>
          {summary.failed > 0 && (
            <Card className="bg-red-50 border-red-200">
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-red-700">{summary.failed}</div>
                <p className="text-sm text-red-600">Failed</p>
              </CardContent>
            </Card>
          )}
          {summary.skipped > 0 && (
            <Card className="bg-gray-50 border-gray-200">
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-gray-700">{summary.skipped}</div>
                <p className="text-sm text-gray-600">Skipped</p>
              </CardContent>
            </Card>
          )}
        </div>

        {hasErrors && (
          <ScrollArea className="h-[100px] border rounded-md p-2">
            <div className="space-y-1">
              {summary.results
                .filter((r) => !r.success)
                .map((result) => (
                  <div
                    key={result.taginnEmployeeId}
                    className="flex items-center gap-2 text-sm text-red-600"
                  >
                    <XCircle className="w-4 h-4" />
                    <span>{result.taginnEmployeeName}: {result.error}</span>
                  </div>
                ))}
            </div>
          </ScrollArea>
        )}

        <DialogFooter>
          <Button onClick={handleClose}>Close</Button>
        </DialogFooter>
      </>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-lg">
        {wizardState.step === 'select' && renderSelectStep()}
        {wizardState.step === 'review' && renderReviewStep()}
        {wizardState.step === 'export' && renderExportStep()}
        {wizardState.step === 'complete' && renderCompleteStep()}
      </DialogContent>
    </Dialog>
  );
};

export default DotyposExportDialog;
